import { useCallback, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_TRANSACTION_DETAILS } from 'api/graphql/queries/transactions/getOrdersByTransactionId';
import { Order, OrderTotals } from 'components/transactions/types';

interface TransactionDetailsResult {
  orders: Order[];
  loading: boolean;
  error: any;
  transactionInfo: {
    transaction_id: string;
    transaction_date: string;
  } | null;
  orderTotals: OrderTotals | undefined;
}

/**
 * Custom hook to fetch orders related to a specific transaction
 * 
 * @param entityTransactionId - The entity transaction ID to fetch orders for
 * @returns Object containing orders data, loading state, and any errors
 */
export function useTransactionDetailsData(entityTransactionId: string): TransactionDetailsResult {
  // State for orders data, transaction info, and order totals
  const [orders, setOrders] = useState<Order[]>([]);
  const [transactionInfo, setTransactionInfo] = useState<{ 
    transaction_id: string; 
    transaction_date: string; 
  } | null>(null);
  const [orderTotals, setOrderTotals] = useState<OrderTotals | undefined>(undefined);
  
  // Query for fetching orders by transaction ID
  const [fetchOrders, { loading, error, data }] = useLazyQuery(
    GET_TRANSACTION_DETAILS, 
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );
  
  // Format date as "MMM D, YYYY" (e.g., "Apr 23, 2025")
  const formatOrderDate = useCallback((dateString: string): string => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  }, []);
  
  // Transform API response to match the Order interface
  // For debugging
  const logOrders = (orders: any[]) => {
    console.log("API Orders data:", orders);
    orders.forEach((order, index) => {
      console.log(`Order ${index}: order_id=${order.order_id}, orderNumber=${order.orderNumber}`);
    });
  };

  const transformOrders = useCallback((apiOrders: any[]): Order[] => {
    // Debug
    logOrders(apiOrders);

    return apiOrders.map((item) => {
      if (!item.order_id) {
        console.warn(`Order ${item.orderNumber} is missing order_id`);
      }
      
      return {
        id: item.orderNumber || '',
        // Explicitly use the order_id from API without fallback
        order_id: item.order_id,
        orderNumber: item.orderNumber || '',
        order_number: item.orderNumber || '',
        date: formatOrderDate(item.orderDate),
        orderDate: formatOrderDate(item.orderDate),
        amount: item.totalAmount || 0,
        totalAmount: item.totalAmount || 0,
        commission: item.commission || 0,
        sales: item.netEarnings || 0,
        netEarnings: item.netEarnings || 0
      };
    });
  }, [formatOrderDate]);
  
  // Fetch orders when entityTransactionId changes
  useEffect(() => {
    if (entityTransactionId) {
      // For the getOrdersByTransactionId query, the transactionId is expected as a String
      // Make sure we're not passing null or undefined
      fetchOrders({
        variables: {
          transactionId: entityTransactionId.toString()
        }
      });
    }
  }, [entityTransactionId, fetchOrders]);
  
  // Update orders state, transaction info, and order totals when API response is received
  useEffect(() => {
    if (data?.getOrdersByTransactionId) {
      if (data.getOrdersByTransactionId.orders) {
        const transformedOrders = transformOrders(data.getOrdersByTransactionId.orders);
        setOrders(transformedOrders);
      } else {
        setOrders([]);
      }
      
      // Set transaction info
      setTransactionInfo({
        transaction_id: data.getOrdersByTransactionId.transaction_id || entityTransactionId,
        transaction_date: formatOrderDate(data.getOrdersByTransactionId.transaction_date) || ''
      });
    } else {
      setOrders([]);
      setTransactionInfo(null);
      setOrderTotals(undefined);
    }
  }, [data, transformOrders, entityTransactionId, formatOrderDate]);
  
  return {
    orders,
    loading,
    error,
    transactionInfo,
    orderTotals
  };
}
